import { useCallback, useEffect, useRef, useState } from "react";

import { ComingUpAPI, type ComingUpItem, type ComingUpScope } from "./api";

export const COMING_UP_POLL_MS = 5 * 60_000;

export type ComingUp = {
  items: ComingUpItem[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
};

/** One scope of `coming-up`, refreshed on a slow poll and when the tab comes back. */
export function useComingUp(scope: ComingUpScope, enabled = true): ComingUp {
  const [items, setItems] = useState<ComingUpItem[]>([]);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const seq = useRef(0);

  const refresh = useCallback(async () => {
    seq.current += 1;
    const mine = seq.current;
    try {
      const next = await ComingUpAPI.list(scope);
      if (mine !== seq.current) return;
      setItems(next);
      setError(null);
    } catch (e) {
      if (mine === seq.current)
        setError((e as Error)?.message ?? "Couldn't load what's coming up");
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [scope]);

  useEffect(() => {
    if (!enabled) return undefined;
    setLoading(true);
    void refresh();
    const t = setInterval(() => void refresh(), COMING_UP_POLL_MS);
    const onVisible = () => {
      if (!document.hidden) void refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, refresh]);

  return { items, loading, error, refresh };
}
